import React, { createContext, useContext, useState } from 'react';

interface AuthModalsContextType {
  showLoginModal: boolean;
  showRegisterModal: boolean;
  showAuthSelectionModal: boolean;
  authFlowMode: 'login' | 'signup';
  openLoginModal: () => void;
  closeLoginModal: () => void;
  openRegisterModal: () => void;
  closeRegisterModal: () => void;
  openAuthSelectionModal: () => void;
  closeAuthSelectionModal: () => void;
  openAuthFlowLogin: () => void;
  openAuthFlowSignup: () => void;
  switchToRegister: () => void;
  switchToLogin: () => void;
}

const AuthModalsContext = createContext<AuthModalsContextType | undefined>(undefined);

export function AuthModalsProvider({ children }: { children: React.ReactNode }) {
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [showAuthSelectionModal, setShowAuthSelectionModal] = useState(false);
  const [authFlowMode, setAuthFlowMode] = useState<'login' | 'signup'>('login');

  const openLoginModal = () => {
    setShowRegisterModal(false);
    setShowAuthSelectionModal(false);
    setShowLoginModal(true);
  };

  const closeLoginModal = () => setShowLoginModal(false);

  const openRegisterModal = () => {
    setShowLoginModal(false);
    setShowAuthSelectionModal(false);
    setShowRegisterModal(true);
  };

  const closeRegisterModal = () => setShowRegisterModal(false);

  const openAuthSelectionModal = () => {
    setShowLoginModal(false);
    setShowRegisterModal(false);
    setShowAuthSelectionModal(true);
  };

  const closeAuthSelectionModal = () => setShowAuthSelectionModal(false);

  const openAuthFlowLogin = () => {
    setAuthFlowMode('login');
    setShowAuthSelectionModal(false);
    setShowLoginModal(true);
    setShowRegisterModal(false);
  };

  const openAuthFlowSignup = () => {
    setAuthFlowMode('signup');
    setShowAuthSelectionModal(false);
    setShowLoginModal(true);
    setShowRegisterModal(false);
  };

  const switchToRegister = () => {
    setShowLoginModal(false);
    setShowRegisterModal(true);
  };

  const switchToLogin = () => {
    setShowRegisterModal(false);
    setShowLoginModal(true);
  };

  return (
    <AuthModalsContext.Provider
      value={{
        showLoginModal,
        showRegisterModal,
        showAuthSelectionModal,
        authFlowMode,
        openLoginModal,
        closeLoginModal,
        openRegisterModal,
        closeRegisterModal,
        openAuthSelectionModal,
        closeAuthSelectionModal,
        openAuthFlowLogin,
        openAuthFlowSignup,
        switchToRegister,
        switchToLogin,
      }}
    >
      {children}
    </AuthModalsContext.Provider>
  );
}

export function useAuthModals() {
  const context = useContext(AuthModalsContext);
  if (!context) {
    throw new Error('useAuthModals must be used within AuthModalsProvider');
  }
  return context;
}
